import type { MeResponse } from '@/api/generated/model';
import type { EstadoDaMarcaDagua } from '@/features/documentos/components/ControleDeMarcaDagua';
import type { Modelo } from '@/features/documentos/rotulos';

/**
 * O papel em HTML, ao vivo.
 *
 * ⚠️ Cores FIXAS da paleta `papel.*`, sem variante de tema — pelo mesmo motivo
 * documentado em `PreviaDaAssinatura`: o documento sai sempre em papel branco,
 * e a prévia tem de sair igual nos dois temas. Com `bg-card`, no tema escuro
 * ela mostraria um papel preto que não existe.
 *
 * ⚠️ E ela é uma APROXIMAÇÃO, não o renderizador. O CSS faz aqui o que o
 * `@react-pdf` faz lá (rotação + opacidade na marca d'água, faixa sangrada,
 * moldura), mas quem prova o resultado é o botão "Prévia em PDF" — que roda o
 * renderizador de verdade. Esta serve para o retorno instantâneo enquanto o
 * médico mexe nos controles, sem custar um render de A4 por clique.
 */
export function PreviaDoLayout({
  modelo,
  cor,
  marcaDagua,
  me,
}: {
  modelo: Modelo;
  cor: string;
  marcaDagua: EstadoDaMarcaDagua;
  me: MeResponse;
}) {
  const { perfil } = me;
  const registro = perfil.crmv
    ? `CRMV ${perfil.crmv}${perfil.ufCrmv ? `-${perfil.ufCrmv}` : ''}`
    : null;

  const contato = [registro, perfil.especialidade, perfil.clinica, perfil.telefone]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium text-brand">Prévia</p>

      <div
        className="relative overflow-hidden rounded-md bg-papel text-papel-texto shadow-sm border border-papel-borda"
        style={{ aspectRatio: '210 / 297' }}
      >
        {/* Atrás de tudo, como no PDF: no @react-pdf a ordem dos filhos é a
            ordem de pintura, e aqui o `-z-0` do irmão faz o mesmo papel. */}
        <MarcaDaguaHtml marcaDagua={marcaDagua} cor={cor} />

        {modelo === 'MOLDURA' && (
          <div
            className="absolute inset-[6px] rounded-[3px] border-2 pointer-events-none"
            style={{ borderColor: cor }}
          />
        )}

        <div className="relative h-full flex flex-col text-[7px] leading-tight">
          <Cabecalho modelo={modelo} cor={cor} nome={perfil.nomeCompleto} clinica={perfil.clinica} contato={contato} logoUrl={perfil.logoUrl} />

          <div className="flex-1 px-3 pb-2 flex flex-col gap-2 min-h-0">
            <Titulo modelo={modelo} cor={cor} />

            <BlocoDoPaciente modelo={modelo} cor={cor} />

            <div className="space-y-1">
              <Regua w="94%" />
              <Regua w="88%" />
              <Regua w="91%" />
            </div>

            <div className="mt-auto flex flex-col items-center gap-1 pb-1">
              <div className="w-[45%] border-t border-papel-texto/70 pt-0.5 text-center">
                <p className="font-semibold truncate">{perfil.nomeCompleto}</p>
                {registro && <p className="text-papel-suave text-[6px]">{registro}</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="text-xs text-muted-foreground">
        Aproximação. Use "Prévia em PDF" para ver o papel de verdade.
      </p>
    </div>
  );
}

function MarcaDaguaHtml({ marcaDagua, cor }: { marcaDagua: EstadoDaMarcaDagua; cor: string }) {
  if (marcaDagua.tipo === 'NENHUMA') return null;
  if (marcaDagua.tipo === 'TEXTO' && !marcaDagua.texto.trim()) return null;
  if (marcaDagua.tipo === 'IMAGEM' && !marcaDagua.url) return null;

  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{ opacity: marcaDagua.opacidade / 100 }}
    >
      <div style={{ transform: `rotate(${marcaDagua.inclinacao}deg)` }}>
        {marcaDagua.tipo === 'TEXTO' ? (
          <span className="text-2xl font-bold whitespace-nowrap" style={{ color: cor }}>
            {marcaDagua.texto}
          </span>
        ) : (
          <img src={marcaDagua.url ?? ''} alt="" className="max-w-[60%] mx-auto" />
        )}
      </div>
    </div>
  );
}

interface PropsDoCabecalho {
  modelo: Modelo;
  cor: string;
  nome: string;
  clinica: string | null;
  contato: string;
  logoUrl: string | null;
}

function Cabecalho({ modelo, cor, nome, clinica, contato, logoUrl }: PropsDoCabecalho) {
  const logo = logoUrl ? (
    <img src={logoUrl} alt="" className="h-4 object-contain" />
  ) : (
    <span className="font-bold" style={{ color: cor }}>
      {clinica ?? nome}
    </span>
  );

  if (modelo === 'FAIXA') {
    return (
      <div className="px-3 py-2 text-white" style={{ background: cor }}>
        <p className="font-bold truncate">{clinica ?? nome}</p>
        <p className="font-semibold truncate">{nome}</p>
        <p className="opacity-80 truncate text-[6px]">{contato}</p>
      </div>
    );
  }

  if (modelo === 'MOLDURA') {
    return (
      <div className="px-3 pt-4 pb-2 text-center space-y-0.5">
        {logo}
        <p className="font-semibold truncate">{nome}</p>
        <p className="text-papel-suave truncate text-[6px]">{contato}</p>
      </div>
    );
  }

  if (modelo === 'MINIMALISTA') {
    return (
      <div className="px-3 pt-4 pb-3 space-y-0.5">
        <p className="font-semibold">{nome}</p>
        <p className="text-papel-suave uppercase tracking-wider truncate text-[6px]">{contato}</p>
      </div>
    );
  }

  // CLASSICO e COMPACTO: logo e médico à esquerda, meta à direita.
  return (
    <div
      className="px-3 pt-3 pb-1.5 flex items-start justify-between gap-2 border-b"
      style={{ borderColor: `${cor}33` }}
    >
      <div className="min-w-0 space-y-0.5">
        {logo}
        <p className="font-semibold truncate">{nome}</p>
        <p className="text-papel-suave truncate text-[6px]">{contato}</p>
      </div>
      <div className="text-papel-suave text-right shrink-0 text-[6px]">
        <p>Nº 2026/0001</p>
        <p>01/01/2026</p>
      </div>
    </div>
  );
}

function Titulo({ modelo, cor }: { modelo: Modelo; cor: string }) {
  const texto = 'Receituário Veterinário';

  if (modelo === 'FAIXA') {
    return (
      <p className="font-bold uppercase tracking-wide" style={{ color: cor }}>
        {texto}
      </p>
    );
  }
  if (modelo === 'MOLDURA') {
    return (
      <p
        className="font-bold text-center border-y py-0.5"
        style={{ color: cor, borderColor: `${cor}33` }}
      >
        {texto}
      </p>
    );
  }
  if (modelo === 'MINIMALISTA') return <p className="text-papel-texto">{texto}</p>;
  if (modelo === 'COMPACTO') {
    return (
      <p className="font-bold text-white self-start px-1 py-0.5 rounded-sm" style={{ background: cor }}>
        {texto}
      </p>
    );
  }
  return (
    <p className="font-bold text-center" style={{ color: cor }}>
      {texto}
    </p>
  );
}

function BlocoDoPaciente({ modelo, cor }: { modelo: Modelo; cor: string }) {
  if (modelo === 'MINIMALISTA') {
    return <p className="text-papel-suave text-[6px]">Amostra · Canino · Fêmea · 27,5 kg</p>;
  }

  if (modelo === 'FAIXA') {
    return (
      <div className="text-[6px]">
        {['Paciente', 'Espécie', 'Tutor'].map((r, i) => (
          <div key={r} className="px-1 py-0.5" style={i % 2 === 0 ? { background: `${cor}14` } : {}}>
            <span className="text-papel-suave">{r}</span>
          </div>
        ))}
      </div>
    );
  }

  if (modelo === 'COMPACTO') {
    return (
      <div className="grid grid-cols-4 gap-px text-[6px]">
        {Array.from({ length: 8 }, (_, i) => (
          <div key={i} className="border px-0.5 py-0.5" style={{ borderColor: `${cor}33` }}>
            <span className="text-papel-suave">—</span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-sm border px-1.5 py-1 space-y-0.5" style={{ borderColor: `${cor}33` }}>
      <p className="uppercase font-bold text-[6px]" style={{ color: cor }}>
        Identificação
      </p>
      <Regua w="70%" />
      <Regua w="55%" />
    </div>
  );
}

function Regua({ w }: { w: string }) {
  return <div className="h-[2px] rounded-full bg-papel-suave/40" style={{ width: w }} />;
}
